import { CollectionHelper } from 'src/app/helpers/collection-helper';
import ProjectHelper from 'src/app/helpers/project-helper';
import Company from 'src/app/model/company/company';
import Contact from 'src/app/model/Contact/contact';
import IPBoardPart from 'src/app/model/product/ip-board/ip-board-part';
import { IPBoardPartPhoneNumber } from 'src/app/model/product/ip-board/ip-board-part-phone-number';
import { Project } from 'src/app/model/project/project';
import { FormatPhoneNumber } from 'src/app/modules/app-pipes/format-phonenumber';

export class IPBoardDesignPickerHelper {
  private constructor() {}

  static readonly OTHER_PHONE_NUMBER_OPTION_ID = 'Ander';

  static calculateIPBoardPartPhoneNumbers(
    ipBoardPart: IPBoardPart,
    project: Project,
    otherContacts: Contact[],
    formatPhoneNumber: FormatPhoneNumber
  ): PhoneNumberWithOptions[] {
    if (
      !project.collaborative ||
      !CollectionHelper.isPopulated(project.projectContacts)
    ) {
      return [];
    }

    const companies = ProjectHelper.getInvolvedCompanies(project);
    const companyToOptionsSet = new Map<number, Set<string>>();
    const seenCompanies = new Map<number, SeenCompanyEntry>(
      companies.map(c => [c.id, { seen: false, regularName: c.regularName }])
    );
    const totalCompanies = companies.length;
    project.projectContacts.forEach(pc => {
      let options = companyToOptionsSet.get(pc.company.id);
      if (options == null) {
        options = new Set<string>();
        companyToOptionsSet.set(pc.company.id, options);
        pc.company.telephones.forEach(t => {
          options.add(formatPhoneNumber.transformTelephone(t));
        });
      }
      pc.contact.telephones.forEach(t => {
        options.add(formatPhoneNumber.transformTelephone(t));
      });
    });
    if (CollectionHelper.isPopulated(otherContacts)) {
      let options = companyToOptionsSet.get(companies[0].id);
      if (options == null) {
        options = new Set<string>();
        companyToOptionsSet.set(companies[0].id, options);
      }
      otherContacts.forEach(c => {
        if (CollectionHelper.isPopulated(c.telephones)) {
          c.telephones.forEach(t => {
            options.add(formatPhoneNumber.transformTelephone(t));
          });
        }
      });
    }

    const companyToPhoneNumberOptions = new Map<number, PhoneNumberOption[]>();
    for (const entry of companyToOptionsSet.entries()) {
      const phoneNumberOptions: PhoneNumberOption[] = [];
      for (const option of entry[1].values()) {
        phoneNumberOptions.push({ id: option, label: option });
      }
      phoneNumberOptions.push({
        id: this.OTHER_PHONE_NUMBER_OPTION_ID,
        label: 'Ander telefoonnummer'
      });
      companyToPhoneNumberOptions.set(entry[0], phoneNumberOptions);
    }

    const toReturn: PhoneNumberWithOptions[] = [];
    let processedCompanies = 0;

    if (
      ipBoardPart != null &&
      CollectionHelper.isPopulated(ipBoardPart.phoneNumbers)
    ) {
      for (const p of ipBoardPart.phoneNumbers) {
        const seenCompany = seenCompanies.get(p.company.id);
        if (seenCompany.seen) {
          continue;
        }
        toReturn.push({
          ipBoardPartPhoneNumber: p,
          options: companyToPhoneNumberOptions.get(p.company.id)
        });
        seenCompany.seen = true;
        if (++processedCompanies === totalCompanies) {
          break;
        }
      }
    }

    if (processedCompanies === totalCompanies) {
      return toReturn;
    }

    for (const entry of seenCompanies.entries()) {
      if (entry[1].seen) {
        continue;
      }
      const phoneNumber = new IPBoardPartPhoneNumber();
      if (ipBoardPart != null && ipBoardPart.id != null) {
        phoneNumber.ipBoardPart = new IPBoardPart();
        phoneNumber.ipBoardPart.id = ipBoardPart.id;
      }
      phoneNumber.company = new Company();
      phoneNumber.company.id = entry[0];
      phoneNumber.company.regularName = entry[1].regularName;
      toReturn.push({
        ipBoardPartPhoneNumber: phoneNumber,
        options: companyToPhoneNumberOptions.get(entry[0])
      });
    }

    return toReturn;
  }
}

interface SeenCompanyEntry {
  seen: boolean;
  regularName: string;
}

export interface PhoneNumberWithOptions {
  ipBoardPartPhoneNumber: IPBoardPartPhoneNumber;
  options: PhoneNumberOption[];
}

export interface PhoneNumberOption {
  id: string;
  label: string;
}
